import { useCallback, type RefObject } from "react";
import type { User } from "@supabase/supabase-js";
import * as orderService from "@/supabase/order/service/orderService";
import { logError } from "@/lib/sentry";
import { toast } from "sonner";
import { OrderStatus } from "@/components/OrderControls";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { CombinedShippingModalHandle } from "@/components/CombinedShippingModalContainer";
import { ConfirmDialogHandle } from "@/components/ConfirmDialogContainer";

interface UseOrderStatusActionsProps {
  user: User | null;
  selectedDate: Date;
  activeCustomer: CustomerTemplate | null | undefined;
  activeCustomerId: string | null;
  customerTemplates: CustomerTemplate[];
  selectedCustomerIds: string[];
  setSelectedCustomerIds: (ids: string[]) => void;
  setIsBatchProcessing: (value: boolean) => void;
  setLocalDateOrderTemplates: (
    updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])
  ) => void;
  extractOrderId: (customerId: string) => number | null;
  // ★ combinedShippingRef는 Home 소유 useRef(L723, P11이 깔아둔 컨테이너 핸들)를 prop 주입. 훅 자체 ref 생성 금지.
  combinedShippingRef: RefObject<CombinedShippingModalHandle>;
  // window.confirm 대체 다이얼로그(ConfirmDialogContainer 핸들) — Home 소유 단일 useRef 주입(동일 규율).
  confirmDialogRef: RefObject<ConfirmDialogHandle>;
  // 명시적 status 쓰기 barrier(useOrderStatusReconcile begin/end 주입) — 낙관 갱신 직전 begin,
  // DB 쓰기 정착 후 end. 자동전환 reconcile이 유저의 명시적 변경을 되돌리는 것 차단(스펙 §16.10).
  onStatusWriteStart?: (orderId: number) => void;
  onStatusWriteEnd?: (orderId: number) => void;
}

interface UseOrderStatusActionsResult {
  proceedWithStatusUpdate: (customerId: string, status: OrderStatus) => Promise<boolean>;
  handleBatchStatusChange: (status: OrderStatus) => Promise<void>;
  handleOrderStatusChange: (status: OrderStatus) => Promise<void>;
}

/**
 * 주문 상태 변경 도메인 훅(P16B). proceedWithStatusUpdate(공유 코어) + 배치(handleBatchStatusChange) +
 * 상세(handleOrderStatusChange) 3플로우를 Home에서 바이트 그대로 이동. keep 해제는 proceedWithStatusUpdate
 * 내부(출고완료+킵 → is_keep:false)에서만 일어남(상세/배치 keep 토글 핸들러는 P16A useOrderKeepActions 몫).
 *
 * 바이트보존 high 계약(잘못 바꾸면 빌드는 통과해도 런타임 데이터 조용히 오류):
 * - proceedWithStatusUpdate 반환 boolean = keepStatusChanged(출고완료 시 킵 해제 여부). 상세 플로우가 토스트에
 *   "('킵' 해제)" 붙일지 결정하는 신호 → boolean을 void로 "정리" 절대 금지. DB 실패 시 Promise.reject(err)로
 *   상위 Promise.allSettled가 reject를 집계하게 하는 것도 계약(배치 successCount 정확도).
 * - 합배송 분기: 출고완료 + 관련주문 존재 시 combinedShippingRef.current?.open(...) 호출 후 early return
 *   (모달이 확정 콜백에서 이어받음). 배치는 모달 열면 setSelectedCustomerIds([]) 건너뜀(=의도). 상세는 catch에서
 *   proceedWithStatusUpdate 단독 폴백(await 없음 = fire-and-forget, 원본 보존).
 * - proceedWithStatusUpdate dep의 activeCustomerId는 body 미참조 vestigial(원본 보존, "정리" 금지).
 *
 * dep 델타(의도): 배치/상세 dep에 setIsBatchProcessing·setSelectedCustomerIds·combinedShippingRef 추가.
 * Home에선 useState 세터/useRef라 exhaustive-deps 면제였으나 prop이 되며 요구됨 → 전부 안정 참조라 behavior-inert
 * (useCallback 재생성 없음, useMoveOrderDate/P15b와 동일 패턴). lint baseline 유지 목적.
 */
export function useOrderStatusActions({
  user,
  selectedDate,
  activeCustomer,
  activeCustomerId,
  customerTemplates,
  selectedCustomerIds,
  setSelectedCustomerIds,
  setIsBatchProcessing,
  setLocalDateOrderTemplates,
  extractOrderId,
  combinedShippingRef,
  confirmDialogRef,
  onStatusWriteStart,
  onStatusWriteEnd,
}: UseOrderStatusActionsProps): UseOrderStatusActionsResult {
  const proceedWithStatusUpdate = useCallback(async (customerId: string, status: OrderStatus): Promise<boolean> => {
    const customer = customerTemplates.find(c => c.id === customerId);
    if (!customer) return false;

    const wasKept = customer.is_keep;
    const isNowShipped = status === '출고 완료';
    const keepStatusChanged = isNowShipped && wasKept;

    // 명시적 status 쓰기 barrier(§16.10) — 낙관 갱신 직전 begin, DB 쓰기 정착 후 end(finally).
    const reconcileOrderId = extractOrderId(customerId);
    if (reconcileOrderId) onStatusWriteStart?.(reconcileOrderId);
    try {
      setLocalDateOrderTemplates(prev =>
        prev.map(cust => {
          if (cust.id === customerId) {
            return {
              ...cust,
              status: status,
              ...(keepStatusChanged && { is_keep: false })
            };
          }
          return cust;
        })
      );

      // activeCustomer status/keep는 위 setLocalDateOrderTemplates 갱신에서 derive로 자동 반영됨.

      const orderId = extractOrderId(customerId);
      if (orderId) {
        try {
          await orderService.updateOrderStatus(orderId, status);
          if (keepStatusChanged) {
            await orderService.updateOrderKeepStatus(orderId, false);
          }
        } catch (err) {
          logError(`DB status/keep update failed for order ${orderId}:`, err);
          toast.error(`'${customer.nickname}'님의 주문 상태 변경에 실패했습니다.`);
          // Optional: Revert local state changes on DB failure
          return Promise.reject(err);
        }
      }
      return keepStatusChanged;
    } finally {
      if (reconcileOrderId) onStatusWriteEnd?.(reconcileOrderId);
    }
  }, [customerTemplates, setLocalDateOrderTemplates, activeCustomerId, extractOrderId, onStatusWriteStart, onStatusWriteEnd]);

  const handleBatchStatusChange = useCallback(async (status: OrderStatus) => {
    if (!user) {
      toast.error("로그인이 필요한 기능입니다.");
      return;
    }
    if (selectedCustomerIds.length === 0) {
      toast.info("선택된 주문이 없습니다.");
      return;
    }

    setIsBatchProcessing(true);
    try {
      // '출고 완료'일 경우에만 합배송 로직 실행
      if (status === '출고 완료') {
        const customersToShip = customerTemplates.filter(c => selectedCustomerIds.includes(c.id));
        const nicknames = [...new Set(customersToShip.map(c => c.nickname))];

        // 부분입금 출고 경고(v1 갭: 합배송 relevantOrders에 포함되는 다른 날짜 주문은 미검사, 선택된 항목만).
        // window.confirm → 다이얼로그(오너 2026-07-06). 대기 중에도 isBatchProcessing 락 유지(finally 해제).
        const partialCount = customersToShip.filter(c => c.status === '부분 입금').length;
        if (partialCount > 0) {
          const ok = confirmDialogRef.current
            ? await confirmDialogRef.current.confirm({
                title: "'부분 입금' 주문을 출고 처리할까요?",
                description: `선택 항목에 '부분 입금' 주문 ${partialCount}건이 있습니다.`,
                warning: "출고 처리하면 '부분 입금' 표시가 사라집니다.",
                confirmLabel: '출고 처리',
                tone: 'warn',
              })
            : window.confirm(
                `선택 항목에 '부분 입금' 주문 ${partialCount}건이 있습니다.\n출고 처리하면 '부분 입금' 표시가 사라집니다. 계속할까요?`
              );
          if (!ok) return; // finally의 setIsBatchProcessing(false)가 락 해제
        }

        const relevantOrdersPromises = nicknames.map(nickname => {
          const excludeOrderIds = customersToShip
            .filter(c => c.nickname === nickname)
            .map(c => extractOrderId(c.id))
            .filter((id): id is number => id !== null);

          return orderService.findRecentRelevantOrdersByNickname(user.id, nickname, selectedDate, ...excludeOrderIds);
        });

        const relevantOrdersResult = await Promise.all(relevantOrdersPromises);
        const allRelevantOrders = relevantOrdersResult.flat();

        if (allRelevantOrders.length > 0) {
          combinedShippingRef.current?.open(allRelevantOrders, { customerId: selectedCustomerIds.join(','), status: '출고 완료' });
          return; // 모달이 닫힐 때까지 여기서 함수 실행 중단
        }
      }

      // '출고 완료'가 아니거나, 합배송 건이 없는 경우
      const confirm = confirmDialogRef.current
        ? await confirmDialogRef.current.confirm({
            title: `선택한 주문 ${selectedCustomerIds.length}건의 상태를 변경할까요?`,
            description: `'${status}' 상태로 일괄 변경됩니다.`,
            confirmLabel: '변경',
            tone: 'positive',
          })
        : window.confirm(`선택된 ${selectedCustomerIds.length}개의 주문을 '${status}' 상태로 변경하시겠습니까?`);
      if (!confirm) return;

      const results = await Promise.allSettled(
        selectedCustomerIds.map(id => proceedWithStatusUpdate(id, status))
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      toast.success(`총 ${selectedCustomerIds.length}개 중 ${successCount}개의 주문 상태를 '${status}'(으)로 변경했습니다.`);

      setSelectedCustomerIds([]);

    } catch (error) {
      logError("일괄 상태 변경 중 오류 발생:", error);
      toast.error("일괄 상태 변경 중 오류가 발생했습니다.");
    } finally {
      setIsBatchProcessing(false);
    }
  }, [user, selectedCustomerIds, customerTemplates, selectedDate, extractOrderId, proceedWithStatusUpdate, setIsBatchProcessing, setSelectedCustomerIds, combinedShippingRef, confirmDialogRef]);

  const handleOrderStatusChange = useCallback(async (status: OrderStatus) => {
    if (!activeCustomer || !activeCustomerId) return;

    // 부분입금 출고 경고(v1 갭: 합배송 모달의 "관련 주문"이 부분입금이어도 여기선 경고 안 함 —
    // 경고는 이 진입점과 handleBatchStatusChange 2곳뿐, relevantOrders 조회 결과는 미검사).
    if (status === '출고 완료' && activeCustomer.status === '부분 입금') {
      const ok = confirmDialogRef.current
        ? await confirmDialogRef.current.confirm({
            title: "'부분 입금' 주문을 출고 처리할까요?",
            description: `'${activeCustomer.nickname}'님 주문은 '부분 입금' 상태입니다.`,
            warning: "출고 처리하면 '부분 입금' 표시가 사라집니다.",
            confirmLabel: '출고 처리',
            tone: 'warn',
          })
        : window.confirm(
            `'${activeCustomer.nickname}'님 주문은 '부분 입금' 상태입니다.\n출고 처리하면 '부분 입금' 표시가 사라집니다. 계속할까요?`
          );
      if (!ok) return;
    }

    if (status === '출고 완료' && user) {
      try {
        const excludeOrderId = extractOrderId(activeCustomerId);
        const orders = await orderService.findRecentRelevantOrdersByNickname(user.id, activeCustomer.nickname, selectedDate, excludeOrderId);

        if (orders.length > 0) {
          combinedShippingRef.current?.open(orders, { customerId: activeCustomerId, status });
        } else {
          const keptChanged = await proceedWithStatusUpdate(activeCustomerId, status);
          let toastMessage = `'${activeCustomer.nickname}'님의 주문이 '출고 완료' 처리되었습니다.`;
          if (keptChanged) {
            toastMessage += " ('킵' 해제)";
          }
          toast.success(toastMessage);
        }
      } catch (error) {
        logError("Error fetching relevant orders for combined shipping:", error);
        toast.error("관련 주문을 불러오는 중 오류가 발생했습니다. 현재 주문만 변경합니다.");
        proceedWithStatusUpdate(activeCustomerId, status);
      }
    } else {
      await proceedWithStatusUpdate(activeCustomerId, status);
      toast.success(`'${activeCustomer.nickname}'님의 주문 상태가 '${status}'(으)로 변경되었습니다.`);
    }
  }, [activeCustomer, activeCustomerId, user, selectedDate, extractOrderId, proceedWithStatusUpdate, combinedShippingRef, confirmDialogRef]);

  return { proceedWithStatusUpdate, handleBatchStatusChange, handleOrderStatusChange };
}
